import { forwardRef, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string | undefined | null;
  alt: string;
  className?: string;
  skeletonClassName?: string;
  objectPosition?: string;
  fallback?: ReactNode;
  /** Decorative mode: no skeleton, instant display for background images */
  decorative?: boolean;
}

// Delay showing skeleton to avoid "flash" when images are cached / load very fast
const PLACEHOLDER_DELAY_MS = 120;

// Module-level cache for loaded image URLs (survives remounts within session)
const loadedImageUrls = new Set<string>();

/**
 * Check if an image URL has been loaded before in this session
 */
const isImageLoaded = (src: string): boolean => {
  if (loadedImageUrls.has(src)) return true;
  
  // Also check browser cache synchronously
  if (typeof window !== 'undefined') {
    try {
      const img = new Image();
      img.src = src;
      if (img.complete && img.naturalWidth > 0) {
        loadedImageUrls.add(src);
        return true;
      }
    } catch {
      // Ignore
    }
  }
  return false;
};

export const OptimizedImage = forwardRef<HTMLDivElement, OptimizedImageProps>(
  function OptimizedImage(
    {
      src,
      alt,
      className,
      skeletonClassName,
      objectPosition = 'center',
      fallback,
      decorative = false,
    },
    forwardedRef
  ) {
    // Initialize loaded state synchronously based on cache
    const [loaded, setLoaded] = useState(() => {
      if (!src) return false;
      return isImageLoaded(src);
    });
    const [error, setError] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useLayoutEffect(() => {
      if (!src) {
        setLoaded(false);
        setError(false);
        setShowSkeleton(false);
        return;
      }

      setError(false);

      // Check if already in our memory cache
      if (loadedImageUrls.has(src)) {
        setLoaded(true);
        setShowSkeleton(false);
        return;
      }

      // If the browser already has the image, mark as loaded BEFORE paint to prevent flicker
      const img = imgRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        loadedImageUrls.add(src);
        setLoaded(true);
        setShowSkeleton(false);
        return;
      }

      // Not loaded yet: keep it hidden, and only show skeleton after a short delay
      setLoaded(false);
      setShowSkeleton(false);

      // For decorative images, skip skeleton entirely
      if (decorative) return;

      const timeoutId = window.setTimeout(() => setShowSkeleton(true), PLACEHOLDER_DELAY_MS);
      return () => window.clearTimeout(timeoutId);
    }, [src, decorative]);

    if (!src || error) {
      return fallback ? <>{fallback}</> : null;
    }

    return (
      <div ref={forwardedRef} className={cn('relative overflow-hidden', className)}>
        {/* Skeleton placeholder (delayed, non-decorative only) */}
        {!decorative && showSkeleton && !loaded && (
          <Skeleton className={cn('absolute inset-0 z-10', skeletonClassName)} />
        )}

        {/* Actual image */}
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={cn(
            'w-full h-full object-cover',
            // Decorative images are ALWAYS visible immediately (no loading transition)
            decorative ? 'opacity-100' : (
              loaded ? 'opacity-100' : 'opacity-0'
            ),
            // Only apply transition for non-decorative images that need to load
            !decorative && !loaded && 'transition-opacity duration-200',
          )}
          style={{ objectPosition }}
          onLoad={() => {
            if (src) loadedImageUrls.add(src);
            setLoaded(true);
            setShowSkeleton(false);
          }}
          onError={() => setError(true)}
          loading={decorative ? 'eager' : 'lazy'}
          decoding="async"
        />
      </div>
    );
  }
);
